/* eslint-disable no-shadow */

import Vue from 'vue';

import Common from '@/store/common';

const operators = [
  { id: 'eq', name: 'Equals' },
  { id: 'eq!', name: 'Doesn\'t Equal' },
  { id: 'con', name: 'Contains' },
  { id: 'sw', name: 'Starts With' },
  { id: 'ew', name: 'Ends With' },
  { id: 'gt', name: 'Greater Than' },
  { id: 'gte', name: 'Greater Than or Equal' },
  { id: 'lt', name: 'Less Than' },
  { id: 'lte', name: 'Less Than or Equal' },
];

const state = {
  pages: [],
  activePage: 0,
  pageSchema: {
    basicInfo: {
      fields: [
        {
          type: 'customInput',
          inputType: 'text',
          styleClasses: 'form__field',
          label: 'Title',
          model: 'title',
          validator: 'string',
          required: true,
        },
        {
          type: 'customInput',
          inputType: 'number',
          styleClasses: 'form__field',
          label: 'Position',
          model: 'position',
        },
        {
          model: '',
          type: 'customSubmit',
          styleClasses: 'form__submit',
          buttonText: 'Add Page',
          onlyOn: 'new',
          onSubmit: (model, schema, context, form) => {
            context.addForm(model, form);
          },
          validateBeforeSubmit: true,
        },
        {
          model: '',
          type: 'customSubmit',
          styleClasses: 'form__submit',
          buttonText: 'Save Page',
          onlyOn: 'edit',
          onSubmit: (model, schema, context, form) => {
            context.editForm(model, form);
          },
          validateBeforeSubmit: true,
        },
      ],
    },
    pageConditions: {
      fields: [
        {
          type: 'customSelect',
          styleClasses: 'form__field',
          label: 'Field',
          model: 'field_id',
          values: [],
          validator: 'integer',
        },
        {
          type: 'customSelect',
          styleClasses: 'form__field',
          label: 'Operator',
          model: 'operator',
          values: operators,
          validator: 'string',
          required: true,
        },
        {
          type: 'customInput',
          inputType: 'text',
          styleClasses: 'form__field',
          label: 'Value',
          model: 'value',
          validator: 'string',
          required: true,
        },
        {
          type: 'customInput',
          inputType: 'number',
          styleClasses: 'form__field',
          label: 'Priority',
          model: 'priority',
          validator: 'integer',
        },
        {
          type: 'customSelect',
          styleClasses: 'form__field',
          label: 'Next Page',
          model: 'next_page_id',
          values: () => {
            return this.a.state.pages
              .filter(page => page.id !== this.a.state.activePage)
              .map(page => ({ id: page.id, name: page.title }));
          },
          validator: 'integer',
        },
        {
          model: '',
          type: 'customSubmit',
          styleClasses: 'form__submit form__submit--first',
          buttonText: 'Save Condition',
          onSubmit: (model, schema, context, form) => {
            context.saveObject(model, form);
          },
          validateBeforeSubmit: true,
        },
        {
          model: '',
          type: 'customSubmit',
          styleClasses: 'form__submit',
          buttonText: 'Cancel',
          visible: (model) => {
            return Object.keys(model).length !== 0;
          },
          onSubmit: (model, schema, context) => {
            context.clearForm();
          },
        },
      ],
    },
  },
};

const getters = {
  activePage: state => state.pages.find(page => page.id === state.activePage) || {},
  activePageId: (state, getters) => {
    const pageId = state.pages.findIndex(page => page.id === getters.activePage.id);
    return pageId >= 0 ? pageId : 0;
  },
  pageConditionId: (state, getters) => (pageCondition) => {
    const pageConditionId = getters.activePage.conditions.findIndex(condition => condition.id === pageCondition);
    return pageConditionId >= 0 ? pageConditionId : 0;
  },
  fieldById: (state, getters, rootState) => fieldId => rootState.admin.field.fields.find(field => field.id === fieldId),
  pageById: state => pageId => state.pages.find(page => page.id === pageId),
  pageIndexById: state => (pageId) => {
    const pageIndex = state.pages.findIndex(page => page.id === pageId);
    return pageIndex >= 0 ? pageIndex : 0;
  },
  friendlyOperatorName: () => operatorId => operators.find(operator => operator.id === operatorId).name,
};

const mutations = {
  setPages(state, pages) {
    state.pages = pages;
  },

  setPageConditions(state, { pageId, conditions }) {
    Vue.set(state.pages[pageId], 'conditions', conditions);
  },

  setActivePage(state, page) {
    state.activePage = page;
  },

  addPage(state, page) {
    state.pages.push(page);
  },

  editPage(state, { pageId, editedPage }) {
    Vue.set(state.pages, pageId, editedPage);
  },

  deletePage(state, { pageId }) {
    Vue.delete(state.pages, pageId);
  },

  addFieldsToDropdown(state, { fields }) {
    Vue.set(state.pageSchema.pageConditions.fields[0], 'values', fields);
  },

  addPageCondition(state, { pageId, pageCondition }) {
    state.pages[pageId].conditions.push(pageCondition);
  },

  editPageCondition(state, { pageId, pageConditionId, pageCondition }) {
    Vue.set(state.pages[pageId].conditions, pageConditionId, pageCondition);
  },

  deletePageCondition(state, { pageId, pageConditionId }) {
    Vue.delete(state.pages[pageId].conditions, pageConditionId);
  },
};

const actions = {
  fetchPages({ commit }, surveyId) {
    return Vue.axios.get(`admin/surveys/${surveyId}/pages`)
      .then((response) => {
        commit('setPages', response.data.page);
      })
      .catch();
  },

  fetchPageConditions({ state, getters, commit }) {
    return Vue.axios.get(`admin/pages/${state.activePage}/conditions`)
      .then((response) => {
        commit('setPageConditions', { pageId: getters.activePageId, conditions: response.data.condition });
      })
      .catch();
  },

  addPage({ state, commit, dispatch }, { data, parentId, form }) {
    commit('addPage', data);
    return Vue.axios.post(`admin/surveys/${parentId}/pages`, data)
      .then(() => dispatch('setMessage', { messageType: 'success' }, { root: true }))
      .catch(error => Common.handleError(dispatch, error.response, form));
  },

  editPage({ getters, commit, dispatch }, { data, form }) {
    commit('editPage', { pageId: getters.activePageId, editedPage: data });
    return Vue.axios.put(`admin/pages/${data.id}`, data)
      .then(() => dispatch('setMessage', { text: `Successfully edited ${data.title}` }, { root: true }))
      .catch(error => Common.handleError(dispatch, error.response, form));
  },

  deletePage({ state, getters, commit, dispatch }, { surveyId, pageId }) {
    commit('deletePage', { pageId: getters.pageIndexById(pageId) });
    return Vue.axios.delete(`admin/surveys/${surveyId}/pages/${pageId}`)
      .then(() => dispatch('setMessage', { text: 'Successfully deleted page' }, { root: true }))
      .catch(error => Common.handleError(dispatch, error.response));
  },

  addPageCondition({ state, getters, commit, dispatch }, { data, form }) {
    commit('addPageCondition', { pageId: getters.activePageId, pageCondition: data });
    return Vue.axios.post(`admin/pages/${state.activePage}/conditions`, data)
      .then(() => dispatch('setMessage', { text: 'Successfully created page condition' }, { root: true }))
      .catch(error => Common.handleError(dispatch, error.response, form));
  },

  editPageCondition({ state, getters, commit, dispatch }, { data, form }) {
    commit('editPageCondition', {
      pageId: getters.activePageId,
      pageConditionId: getters.pageConditionId(data.id),
      pageCondition: data,
    });
    return Vue.axios.put(`admin/pages/${state.activePage}/conditions/${data.id}`, data)
      .then(() => {
        dispatch('setMessage', {
          text: `Successfully edited condition for ${getters.fieldById(data.field_id).title}`,
        }, { root: true });
      }).catch(error => Common.handleError(dispatch, error.response, form));
  },

  deletePageCondition({ state, getters, commit, dispatch }, { inlineId }) {
    commit('deletePageCondition', {
      pageId: getters.activePageId,
      pageConditionId: getters.pageConditionId(inlineId),
    });
    return Vue.axios.delete(`admin/pages/${state.activePage}/conditions/${inlineId}`)
      .then(() => dispatch('setMessage', { text: 'Successfully deleted page condition' }, { root: true }))
      .catch(error => Common.handleError(dispatch, error.response));
  },

  addFieldsToDropdown({ rootState, commit }) {
    const fields = rootState.admin.field.fields
      .map(field => ({ id: field.id, name: `${field.title} (${field.field_type})` }));
    commit('addFieldsToDropdown', { fields });
  },
};

export default {
  state,
  getters,
  mutations,
  actions,
};
